import { KeyboardDatePicker } from "@material-ui/pickers";
import { PDFDownloadLink } from "@react-pdf/renderer";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import Select from "react-select";
import PDFRelatorioVenda from "../../pdfs/PDFRelatorioVendas";
import { ListarEstabelecimentosSemPaginacao } from "../../providers/EstabelecimentoProvider";
import {
  getInfoFinanceiraEstabelecimento,
  getInfoFinanceiras,
} from "../../providers/InfoFinanceiraProvider";
import { listarPedidosByDateRange } from "../../providers/PedidoProvider";

function FinanceiroGraficos() {
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [estabelecimentoSelecionado, setEstabelecimentoSelecionado] = useState();
  const [dataInicio, setDataInicio] = useState(moment().subtract(7, "days"));
  const [dataFim, setDataFim] = useState(moment());
  const [graficoData, setGraficoData] = useState({
    labels: [],
    vendasDia: [],
    faturamentoDia: [],
    clientesDia: [],
  });
  const [infosBasicas, setInfosBasicas] = useState({});
  const [faturamentoRelatorio, setFaturamentoRelatorio] = useState({});
  const [taxa, setTaxa] = useState(0);

  useEffect(() => {
    (async () => {
      const docs = await ListarEstabelecimentosSemPaginacao();
      const mappedOptions = docs.map((d) => ({
        label: d.nome_fantasia,
        value: d._id,
      }));
      setEstabelecimentos(mappedOptions);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const taxas = await getInfoFinanceiras();
      const somaTaxa = taxas?.reduce((acc, i) => (acc += i.taxa_operacao), 0);
      const taxaMedia = somaTaxa
        ? parseFloat(parseFloat(somaTaxa / taxas.length).toFixed(2))
        : 0;
      setTaxa(taxaMedia);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!estabelecimentoSelecionado) return;
      const taxa = await getInfoFinanceiraEstabelecimento(
        estabelecimentoSelecionado.value,
      );
      setTaxa(taxa?.taxa_operacao || 0);
    })();
  }, [estabelecimentoSelecionado]);

  function calcRecorrencia(pedidos) {
    let recorrencia = [];
    pedidos.forEach((pedido) => {
      if (pedido.cliente) {
        if (!recorrencia.some((r) => r.cliente._id === pedido.cliente._id)) {
          recorrencia.push({
            cliente: pedido.cliente,
            pedidos: [pedido],
            qtd_pedidos: 1,
            data_primeiro_pedido: new Date(pedido.createdAt),
          });
        } else {
          recorrencia.forEach((r) => {
            if (r.cliente._id === pedido.cliente._id) {
              r.pedidos.push(pedido);
              r.qtd_pedidos += 1;
            }
          });
        }
      }
    });
    return recorrencia;
  }

  function calcularInfosBasicas(dados, recorrencia) {
    let total_faturado = parseFloat(
      dados.reduce((total, pedido) => (total += pedido.valor_total), 0) || 0,
    ).toFixed(2);
    let clientes_unica_compra = recorrencia.filter((r) => r.qtd_pedidos === 1).length;
    setInfosBasicas({
      quantidade: dados.length,
      total: parseFloat(total_faturado).toFixed(2),
      total_ifeira: parseFloat(total_faturado * taxa).toFixed(2),
      ticket: parseFloat(total_faturado / dados.length).toFixed(2),
      total_clientes: recorrencia.length,
      taxa_recorrencia: parseFloat(dados.length / recorrencia.length || 0).toFixed(2),
      clientes_unica_compra,
      cliente_recorrentes: recorrencia.length - clientes_unica_compra,
    });
  }

  const formatFaturamentoRelatorio = (faturamento, p) => {
    faturamento.total_vendas += 1;
    faturamento.total_faturado += p.valor_total;
    faturamento.subtotal_faturado += p.detalhes_pagamento.subtotal;
    if (
      p.detalhes_pagamento.frete &&
      p.metodo_pagamento.metodo === "pagamento_na_entrega"
    ) {
      faturamento.total_fretes += p.detalhes_pagamento.frete;
      faturamento.quantidade_fretes += 1;
    }
    if (p.metodo_pagamento.tipo === "dinheiro") {
      faturamento.total_fretes_dinheiro += p.detalhes_pagamento.frete;
      faturamento.quantidade_fretes_dinheiro += 1;
    } else {
      faturamento.total_fretes_cartao += p.detalhes_pagamento.frete;
      faturamento.quantidade_fretes_cartao += 1;
    }
    faturamento.total_estabelecimento = faturamento.subtotal_faturado * (1 - taxa);
    faturamento.total_ifeira = faturamento.subtotal_faturado * taxa;

    p.total_ifeira = parseFloat(p.detalhes_pagamento.subtotal * taxa)
      .toFixed(2)
      .toString()
      .replace(".", ",");
    p.total_estabelecimento = parseFloat(p.detalhes_pagamento.subtotal * (1 - taxa))
      .toFixed(2)
      .toString()
      .replace(".", ",");

    return faturamento;
  };

  const calcularFaturamento = async () => {
    const datas = [];
    let faturamentoRelatorio = {
      total_faturado: 0,
      subtotal_faturado: 0,
      total_vendas: 0,
      total_fretes: 0,
      total_estabelecimento: 0,
      total_ifeira: 0,
      quantidade_fretes: 0,
      total_fretes_cartao: 0,
      total_fretes_dinheiro: 0,
      quantidade_fretes_cartao: 0,
      quantidade_fretes_dinheiro: 0,
      data: [],
    };
    let mapPedido = new Map();
    let mapVendas = new Map();
    let mapClientes = new Map();

    const pedidos = await listarPedidosByDateRange(
      dataInicio,
      dataFim,
      estabelecimentoSelecionado?.value,
    );

    faturamentoRelatorio.data = pedidos;

    const groupByMonth = dataFim.diff(dataInicio, "days") > 31;

    const getDateKey = (date) =>
      groupByMonth
        ? date.getMonth() + 1 + "/" + date.getFullYear()
        : date.getDate() + "/" + (date.getMonth() + 1);

    pedidos.forEach((p) => {
      const date = p.agendado ? new Date(p.dataAgendamento) : new Date(p.createdAt);
      const dateKey = getDateKey(date);
      if (dataInicio && dataFim) {
        datas.push(date);
        if (mapPedido[dateKey] > 0) {
          mapPedido[dateKey] += p.valor_total;
        } else {
          mapPedido[dateKey] = parseFloat(p.valor_total.toFixed(2));
        }
        if (mapVendas[dateKey] > 0) {
          mapVendas[dateKey] += 1;
        } else {
          mapVendas[dateKey] = 1;
        }
        formatFaturamentoRelatorio(faturamentoRelatorio, p);
      }
    });

    let recorrencia = calcRecorrencia(pedidos);
    calcularInfosBasicas(pedidos, recorrencia);

    recorrencia.forEach((r) => {
      const dateKey = getDateKey(r.data_primeiro_pedido);
      if (mapClientes[dateKey] > 0) {
        mapClientes[dateKey] += 1;
      } else {
        mapClientes[dateKey] = 1;
      }
    });

    let vendas_dia = Object.keys(mapVendas)
      .map((k) => mapVendas[k])
      .reverse();
    let pedido_grafico = Object.keys(mapPedido)
      .map((k) => mapPedido[k])
      .reverse();
    let clientes_dia = Object.keys(mapClientes)
      .map((k) => mapClientes[k])
      .reverse();

    setGraficoData({
      vendasDia: vendas_dia,
      faturamentoDia: pedido_grafico,
      clientesDia: clientes_dia,
      labels: [
        ...new Set(
          datas
            .sort((a, b) => (a < b ? -1 : 1))
            .map((d) =>
              groupByMonth
                ? d.getMonth() + 1 + "/" + d.getFullYear()
                : d.getDate() + "/" + (d.getMonth() + 1) + "/" + d.getFullYear(),
            ),
        ),
      ],
    });
    setFaturamentoRelatorio(faturamentoRelatorio);
  };

  return (
    <div>
      <div style={{ margin: 10, marginLeft: 50, marginRight: 50 }}>
        <br></br>
        <Row>
          <Col md={9}>
            <h3>Gráficos Financeiros</h3>
          </Col>
        </Row>
        <br></br>
        <p style={{ marginBottom: 0, fontWeight: "bold" }}>Filtros: </p>
        <Row>
          <Col md={4}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}
              >
                <p style={{ marginRight: 30 }}>De</p>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  margin="normal"
                  id="date-picker-inline"
                  label="Data inicial"
                  value={dataInicio}
                  style={{ width: "60%" }}
                  maxDate={new Date()}
                  onChange={(d) => setDataInicio(d)}
                />
              </div>
              <div
                style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}
              >
                <p style={{ marginRight: 30 }}>Até</p>
                <KeyboardDatePicker
                  disableToolbar
                  disabled={!dataInicio}
                  variant="inline"
                  margin="normal"
                  id="date-picker-inline"
                  label="Data final"
                  minDate={dataInicio}
                  maxDate={new Date()}
                  value={dataFim}
                  style={{ width: "60%" }}
                  onChange={(d) => setDataFim(d)}
                />
              </div>
            </div>
          </Col>
          <Col md={4}>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Selecione o Estabelecimento</Form.Label>
                  <Select
                    isDisabled={!estabelecimentos?.length}
                    value={estabelecimentoSelecionado}
                    isClearable
                    isSearchable
                    onChange={(opt) => {
                      setEstabelecimentoSelecionado(opt);
                    }}
                    options={estabelecimentos}
                    placeholder="Estabelecimento"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
          <Col style={{ marginLeft: 14 }}>
            <p style={{ margin: 0, marginBottom: 8 }}>Ações</p>
            <Row>
              <Button onClick={() => calcularFaturamento()}>Aplicar</Button>
              <Button
                variant="danger"
                disabled={!dataInicio || !dataFim}
                style={{ marginLeft: 5 }}
                onClick={() => {
                  setDataInicio(null);
                  setDataFim(null);
                  setEstabelecimentoSelecionado(null);
                  setGraficoData({});
                  setInfosBasicas({});
                  setFaturamentoRelatorio({});
                }}
              >
                Limpar
              </Button>
            </Row>
          </Col>
        </Row>
        <br></br>
        <Row>
          <Col md={6}>
            <h4>N° Vendas</h4>
            <Bar
              data={{
                labels: graficoData.labels,
                datasets: [
                  {
                    type: "line",
                    label: "Quantidade de Vendas",
                    borderColor: "rgb(255, 99, 132)",
                    borderWidth: 2,
                    data: graficoData.vendasDia,
                  },
                ],
              }}
              options={{
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        min: 0,
                        stepSize: 5,
                      },
                    },
                  ],
                },
              }}
            />
          </Col>
          <Col md={6}>
            <h4>Faturamento</h4>
            <Bar
              data={{
                labels: graficoData.labels,
                datasets: [
                  {
                    type: "bar",
                    label: "Faturamento",
                    fill: true,
                    borderColor: "#701B8C",
                    borderWidth: 1,
                    hoverBorderColor: "#ffffff",
                    data: graficoData.faturamentoDia,
                  },
                ],
              }}
              options={{
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        min: 0,
                        stepSize: 5,
                      },
                    },
                  ],
                },
              }}
            />
          </Col>
        </Row>
        <br></br>
        <Row>
          <Col>
            <h4>Aquisição de clientes</h4>
            <Bar
              data={{
                labels: graficoData.labels,
                datasets: [
                  {
                    type: "bar",
                    label: "Clientes Novos",
                    fill: true,
                    borderColor: "#701B8C",
                    borderWidth: 1,
                    hoverBorderColor: "#ffffff",
                    data: graficoData.clientesDia,
                  },
                ],
              }}
              options={{
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        stepSize: 1000,
                      },
                    },
                  ],
                },
              }}
            />
          </Col>
          <Col>
            <h4>Detalhamento</h4>
            <br></br>
            <ul>
              <li>
                <b>Quantidade de pedidos:</b> {infosBasicas.quantidade}
              </li>
              <li>
                <b>Total Faturado:</b> R$ {infosBasicas.total}
              </li>
              <li>
                <b>Faturamento iFeira ({taxa * 100}%)</b>: R$ {infosBasicas.total_ifeira}
              </li>
              <li>
                <b>Ticket médio:</b> R$ {infosBasicas.ticket}
              </li>
              <li>
                <b>Taxa de recorrência:</b> {infosBasicas.taxa_recorrencia}
              </li>
              <li>
                <b>Quantidade de clientes únicos:</b> {infosBasicas.total_clientes}
              </li>
              <li>
                <b>Quantidade de clientes que compraram apenas uma vez: </b>
                {infosBasicas.clientes_unica_compra}
              </li>
              <li>
                <b>Quantidade de clientes que compraram mais de uma vez: </b>
                {infosBasicas.cliente_recorrentes}
              </li>
            </ul>
            <br></br>
            {!!Object.keys(faturamentoRelatorio).length && (
              <PDFDownloadLink
                document={
                  <PDFRelatorioVenda
                    estabelecimento={estabelecimentoSelecionado?.label}
                    dataInicio={dataInicio}
                    dataFim={dataFim}
                    ticketMedio={infosBasicas.ticket}
                    faturamento={faturamentoRelatorio}
                  />
                }
                fileName={`relatorio-vendas-${new Date().toLocaleString()}.pdf`}
                style={{
                  textDecoration: "none",
                  padding: "10px",
                  color: "white",
                  backgroundColor: "#701B8C",
                  border: "1px solid white",
                }}
              >
                Emitir Relatório
              </PDFDownloadLink>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default FinanceiroGraficos;
